import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/api/query-keys';

import { approveWarrantyClaim, getWarrantyClaim, rejectWarrantyClaim } from './api';
import type { RejectWarrantyClaimValues } from './types';

/** Query-key factory for the warranty sub-module — one key space shared by the detail query and both
 *  decision mutations. The queue's `<DataTable>` is keyed under `all` too, via `queryKeyPrefix`. */
export const warrantyKeys = {
  all: queryKeys.warrantyClaims.all,
  detail: (id: string) => [...warrantyKeys.all, 'detail', id] as const,
  /** Device filter typeahead, keyed by search term so a new term resets the cursor. */
  metaDevices: (search: string) => queryKeys.devices.meta('warranty', { search }),
};

/** One warranty claim's detail; idle while `id` is empty, so a closed drawer fetches nothing. */
export function useWarrantyClaim(id: string) {
  return useQuery({
    queryKey: warrantyKeys.detail(id),
    queryFn: () => getWarrantyClaim(id),
    enabled: Boolean(id),
  });
}

/** Both decisions bust the same caches. The real backend's approve/reject responses are slim
 *  (`{id, status}`), never a detail, so the claim's detail is **invalidated** rather than seeded —
 *  an open drawer refetches and shows the new status (and, for a rejection, its Rejection Reason).
 *
 *  The queue's `<DataTable>` is keyed under `warrantyKeys.all` (its `queryKeyPrefix`), so one root
 *  refreshes the list and the detail. One helper so the two mutations cannot drift. */
function syncDecisionCaches(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: warrantyKeys.detail(id) });
  void qc.invalidateQueries({ queryKey: warrantyKeys.all });
  // The claim's Order Details warranty panel (order id unknown here) and the Dashboard's open-request counts.
  void qc.invalidateQueries({ queryKey: queryKeys.orders.all });
  void qc.invalidateQueries({ queryKey: queryKeys.dashboard.quickActions });
}

/** Rejects the claim with the admin's reason. Per-claim hook: the reject modal renders for one claim,
 *  so the id is known at hook time. */
export function useRejectWarrantyClaim(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: RejectWarrantyClaimValues) => rejectWarrantyClaim(id, values),
    onSuccess: () => syncDecisionCaches(qc, id),
  });
}

/** Approves the claim. The id is a **mutate argument** here, not a hook argument: approve fires from
 *  an imperative `useConfirm()` dialog that any row's kebab can open, so it is only known at call
 *  time. */
export function useApproveWarrantyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveWarrantyClaim(id),
    onSuccess: (_result, id) => syncDecisionCaches(qc, id),
  });
}
